import { getPageTitle } from "./pageTitles";

/** Page keys used by the Server Logs page dropdown. Matchers use pathname only (no query). */
export const PAGE_FILTER_ALL = "all";
export const PAGE_FILTER_NONE = "none";
/** Always listed on Server Logs; matches scheduled monthly invoice job lines. */
export const CRON_LOG_PAGE = "/cron/monthly-invoice";
export const CRON_USER_ACTIVITY_PURGE_PAGE = "/cron/user-activity-purge";
export const SCHEDULED_LOG_USER = "scheduled";
export const CRON_FILTER_ALL = "all";
export const CRON_FILTER_ANY = "cron";
export const CRON_JOB_FILTERS = [
  { value: CRON_LOG_PAGE, label: "Monthly invoice" },
  { value: CRON_USER_ACTIVITY_PURGE_PAGE, label: "User-activity purge" },
];

export const SERVER_LOG_PAGE_FILTERS = [
  { value: "dashboard", label: "Dashboard", match: (path) => path === "/" },
  { value: "login", label: "Login", match: (path) => path === "/login" },
  { value: "clients", label: "Clients", match: (path) => path === "/clients" },
  {
    value: "client-configuration",
    label: "Client Configuration",
    match: (path) => /^\/clients\/[^/]+$/.test(path),
  },
  {
    value: "po-jackets",
    label: "PO and Jacket Management",
    match: (path) => /^\/clients\/[^/]+\/po-jackets$/.test(path),
  },
  {
    value: "billing-profile",
    label: "Invoice Profile Configuration",
    match: (path) => path === "/billing-profile",
  },
  {
    value: "contracts-billing",
    label: "Contracts & Billing Setup",
    match: (path) => path === "/contracts-billing",
  },
  {
    value: "activity-entry",
    label: "Monthly Activity Entry",
    match: (path) => path === "/activity-entry",
  },
  { value: "invoices", label: "Invoices", match: (path) => path === "/invoices" },
  {
    value: "generate-invoice",
    label: "Generate Invoice",
    match: (path) => path === "/invoices/new/review",
  },
  {
    value: "draft-invoice-review",
    label: "Draft Invoice Review",
    match: (path) => /^\/invoices\/[^/]+\/review$/.test(path),
  },
  {
    value: "invoice-details",
    label: "Invoice Details",
    match: (path) => /^\/invoices\/[^/]+\/view$/.test(path),
  },
  {
    value: "accounting-allocation",
    label: "Accounting Allocation",
    match: (path) => path === "/accounting-allocation",
  },
  {
    value: "accounting-allocation-rules",
    label: "Accounting Allocation Rules",
    match: (path) => path === "/accounting-allocation/rules",
  },
  {
    value: "invoice-allocation-detail",
    label: "Invoice Allocation Detail",
    match: (path) => path === "/accounting-allocation/detail",
  },
  { value: "reports", label: "Reports", match: (path) => path === "/reports" },
  {
    value: "offer-catalog",
    label: "Offer Catalog",
    match: (path) => path === "/service-catalog",
  },
  {
    value: "user-activity",
    label: "Recent User Activity",
    match: (path) => path === "/user-activity",
  },
  { value: "create-role", label: "Create New Role", match: (path) => path === "/roles/create" },
  {
    value: "create-privilege",
    label: "Create New Privilege",
    match: (path) => path === "/role-privileges/create",
  },
  {
    value: "assigned-role-privileges",
    label: "Assigned Role Privileges",
    match: (path) => path === "/role-privileges",
  },
  {
    value: "server-logs",
    label: "Server Logs",
    match: (path) => path === "/admin/server-logs",
  },
  {
    value: "automated-invoice-job",
    label: "Automated Invoice Create Job",
    match: (path) => path === "/admin/automated-invoice-job",
  },
];

export function logLinePathname(page) {
  const raw = String(page ?? "").trim();
  if (!raw || raw === "-") {
    return "";
  }
  const cut = raw.indexOf("?");
  const path = (cut >= 0 ? raw.slice(0, cut) : raw).trim();
  if (!path) {
    return "";
  }
  return path === "/" ? "/" : path.replace(/\/+$/, "") || "/";
}

export function isCronLogPage(page) {
  return logLinePathname(page).startsWith("/cron/");
}

export function cronFilterLabel(filter) {
  if (!filter || filter === CRON_FILTER_ALL) {
    return "";
  }
  if (filter === CRON_FILTER_ANY) {
    return "any cron job";
  }
  const job = CRON_JOB_FILTERS.find((item) => item.value === filter);
  if (job) {
    return job.label;
  }
  return formatLogPageOptionLabel(filter);
}

export function matchesPageFilter(page, filter) {
  if (!filter || filter === PAGE_FILTER_ALL) {
    return true;
  }
  const path = logLinePathname(page);
  if (filter === PAGE_FILTER_NONE) {
    return path === "";
  }
  const rule = SERVER_LOG_PAGE_FILTERS.find((item) => item.value === filter);
  if (!rule) {
    return String(page ?? "").trim() === filter || path === filter;
  }
  return rule.match(path);
}

export function pageFilterLabel(filter) {
  if (!filter || filter === PAGE_FILTER_ALL) {
    return "";
  }
  if (filter === PAGE_FILTER_NONE) {
    return "no page";
  }
  const rule = SERVER_LOG_PAGE_FILTERS.find((item) => item.value === filter);
  if (rule) {
    return rule.label;
  }
  return formatLogPageOptionLabel(filter);
}

export function formatLogPageOptionLabel(page) {
  const raw = String(page ?? "").trim();
  if (!raw) {
    return "No page";
  }
  const path = logLinePathname(raw) || raw;
  const title = getPageTitle(path);
  if (title && title !== "Hibbert Accounting") {
    return `${title} (${raw})`;
  }
  return raw;
}

export const USER_FILTER_ALL = "all";
export const USER_FILTER_NONE = "none";
export const LOGGER_FILTER_ALL = "all";
export const LOGGER_FILTER_NONE = "none";

export function lineUserName(line) {
  return String(line?.userName ?? line?.user_name ?? line?.user ?? "").trim();
}

export function lineLogger(line) {
  return String(line?.logger ?? "").trim();
}

export function matchesUserFilter(userName, filter) {
  if (!filter || filter === USER_FILTER_ALL) {
    return true;
  }
  const name = String(userName ?? "").trim();
  if (filter === USER_FILTER_NONE) {
    return name === "";
  }
  return name === filter;
}

export function userFilterLabel(filter) {
  if (!filter || filter === USER_FILTER_ALL) {
    return "";
  }
  if (filter === USER_FILTER_NONE) {
    return "no user";
  }
  if (filter === SCHEDULED_LOG_USER) {
    return "scheduled (cron)";
  }
  return filter;
}

export function loggerFilterLabel(filter) {
  if (!filter || filter === LOGGER_FILTER_ALL) {
    return "";
  }
  if (filter === LOGGER_FILTER_NONE) {
    return "no logger";
  }
  return filter;
}

export function groupLogEvents(lines) {
  const events = [];
  let current = [];
  for (const line of Array.isArray(lines) ? lines : []) {
    if (!line?.stack) {
      if (current.length > 0) {
        events.push(current);
      }
      current = [line];
    } else if (current.length === 0) {
      current = [line];
    } else {
      current.push(line);
    }
  }
  if (current.length > 0) {
    events.push(current);
  }
  return events;
}

export function localIsoDate(date = new Date()) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** Matches backend log file retention (Logback maxHistory plus today). */
export const SERVER_LOG_RETENTION_DAYS = 14;
export const DATE_FILTER_ALL = "all";

export function serverLogDayOptions(today = localIsoDate(), retentionDays = SERVER_LOG_RETENTION_DAYS) {
  const days = [];
  const [year, month, day] = String(today)
    .split("-")
    .map((part) => Number(part));
  if (!year || !month || !day) {
    return days;
  }
  const cursor = new Date(year, month - 1, day);
  for (let i = 0; i <= retentionDays; i += 1) {
    days.push(localIsoDate(cursor));
    cursor.setDate(cursor.getDate() - 1);
  }
  return days;
}

export function serverLogDateLabel(date, today = localIsoDate(), fromDate, toDate) {
  if (!date || date === DATE_FILTER_ALL) {
    if (fromDate && toDate) {
      return `${fromDate} through ${toDate}`;
    }
    return "the last 14 days";
  }
  if (date === today) {
    return `${date} (today)`;
  }
  return date;
}
